/**
 * The user's most recent Hairo conversation, kept in localStorage so that
 * closing the panel, an idle-parked socket or a page refresh never loses it:
 * Hairo reopens on it until the user clicks New chat.
 *
 * Stored per user (`copilot:lastSession:<userId>`) as { id, subject }, where
 * subject is the session's OWN subject (or null), so a resume reconnects with
 * the subject the chat was opened on. Only ids are stored, never messages:
 * history is fetched from the server on resume. Every access is wrapped, since
 * storage can be blocked (private mode, disabled site data).
 */

import { NEW_SESSION } from "./constants";
import { subjectFromSession } from "./subjects";

export const LAST_SESSION_KEY = "copilot:lastSession";

export const lastSessionKey = (userId) =>
  userId === null || userId === undefined || userId === ""
    ? LAST_SESSION_KEY
    : `${LAST_SESSION_KEY}:${userId}`;

const cleanSubject = (subject) =>
  subject?.kind && subject?.id !== null && subject?.id !== undefined
    ? { kind: subject.kind, id: subject.id }
    : null;

/**
 * @returns {{ id: string, subject: object|null } | null} id is NEW_SESSION
 * when the user's last act was New chat (a blank chat with no id yet).
 */
export function readLastSession(userId) {
  try {
    const raw = window.localStorage.getItem(lastSessionKey(userId));
    if (!raw) return null;
    const value = JSON.parse(raw);
    if (!value?.id) return null;
    if (String(value.id) === NEW_SESSION) return { id: NEW_SESSION, subject: null };
    return { id: String(value.id), subject: cleanSubject(value.subject) };
  } catch {
    return null;
  }
}

export function writeLastSession(userId, sessionId, subject) {
  if (!sessionId || String(sessionId) === NEW_SESSION) return;
  try {
    window.localStorage.setItem(
      lastSessionKey(userId),
      JSON.stringify({ id: String(sessionId), subject: cleanSubject(subject) })
    );
  } catch {
    // Storage unavailable: the in-memory record still covers this tab.
  }
}

/** New chat: remember that the user wants a blank chat, not the last one. */
export function markNewChat(userId) {
  try {
    window.localStorage.setItem(
      lastSessionKey(userId),
      JSON.stringify({ id: NEW_SESSION, subject: null })
    );
  } catch {
    // Storage unavailable: the in-memory marker still covers this tab.
  }
}

/**
 * The newest row of a /copilot/sessions list, whatever its subject, as
 * { id, subject }. Server order is not relied on. null when there is none.
 */
export function newestSession(sessions) {
  if (!Array.isArray(sessions)) return null;
  let best = null;
  let bestAt = -Infinity;
  for (const session of sessions) {
    if (!session?.id) continue;
    const at = Date.parse(session.updated_at);
    const when = Number.isFinite(at) ? at : -Infinity;
    if (!best || when > bestAt) {
      best = session;
      bestAt = when;
    }
  }
  return best ? { id: String(best.id), subject: subjectFromSession(best) } : null;
}
